import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useCopilotLeftMenuCollapsed } from '@/pages/aigc/pages/Agent/hooks/useCopilotLeftMenuCollapsed';
import { getLocale, useIntl, useLocation, Redirect, useSelector } from 'umi';
import { ThemeContext } from '@/pages/home/wrappers/common';
import StatusLoading from '@/pages/home/components/StatusLoading';
import {
  SYSTEM_THEME_DARK,
  LANGUAGE_CN,
} from '@/pages/home/_config/config';
import { buriedPoint } from '@home/utils/buriedPoint';
import ReportToolbar from './components/ReportToolbar';
import './index.scss';

/**
 * AI Gallery Report 嵌入页
 *
 * 与子页面（path 指向的 gallery report 页）的对接协议复用 AIGallery：
 * 1) 初始通过 URL query 传递：?theme=light|dark&lang=zh|en（追加在 path 末尾）
 * 2) 主题 / 语言 / Copilot 内嵌侧栏态通过 postMessage 通知，消息体：
 *      { source: 'databrain-main-frame', type: 'theme', value: 'light' | 'dark' }
 *      { source: 'databrain-main-frame', type: 'lang',  value: 'zh'    | 'en'   }
 *      { source: 'databrain-main-frame', type: 'sidebarCollapsed', value: true | false }
 *
 * 安全约束：宿主 query 参数 path 必须以 `/as/report/` 开头，否则重定向到 /aigallery/home，
 * 防止 open redirect / 跨域 iframe 加载。
 */

const REPORT_PATH_PREFIX = '/as/report/';
const FALLBACK_REDIRECT_PATH = '/aigallery/home';

const AI_GALLERY_TARGET_ORIGIN =
  typeof window !== 'undefined' ? window.location.origin : '*';

const themeToParam = (v: string): 'light' | 'dark' =>
  v === SYSTEM_THEME_DARK ? 'dark' : 'light';

const langToParam = (v: string): 'zh' | 'en' =>
  v === LANGUAGE_CN ? 'zh' : 'en';

const isValidReportPath = (raw: unknown): raw is string =>
  typeof raw === 'string' && raw.startsWith(REPORT_PATH_PREFIX);

const buildInitialSrc = (
  rawPath: string,
  theme: 'light' | 'dark',
  lang: 'zh' | 'en',
): string => {
  const sep = rawPath.includes('?') ? '&' : '?';
  return `${rawPath}${sep}theme=${theme}&lang=${lang}`;
};

// 路由 base 为 /v2，但部分环境 / 兼容场景下 location.pathname 可能仍带 /v2 前缀，
// 上报前统一去掉，避免污染 pagePath 维度。
const stripV2Prefix = (pathname: string): string => {
  if (pathname === '/v2') return '/';
  if (pathname.startsWith('/v2/')) return pathname.slice(3);
  return pathname;
};

/**
 * AI Gallery Report 嵌入页组件
 * 顶部工具条提供「AI编辑」（拉起 Copilot 修改报告）与「下载」（纯 HTML 或 ZIP 打包）能力
 */
const AIGalleryReport: React.FC = () => {
  useIntl();
  const location: any = useLocation();
  const { themeValue } = useContext(ThemeContext);
  const lang = langToParam(getLocale());
  const theme = themeToParam(themeValue);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [leftMenuCollapsed] = useCopilotLeftMenuCollapsed();

  const { userInfo } = useSelector((state: any) => ({
    userInfo: state.home.userInfo,
  }));
  const isUserInfoReady = !!userInfo;

  const rawPath = location.query?.path;
  const validPath = isValidReportPath(rawPath) ? rawPath : '';

  const rawName = location.query?.name;
  const pageName = typeof rawName === 'string' ? rawName : '';

  const initialSrc = useMemo(() => {
    if (!validPath) return '';
    return buildInitialSrc(validPath, theme, lang);
    // 仅在 path 变化时重算 src（theme/lang 通过 postMessage 同步）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validPath]);

  useEffect(() => {
    if (!validPath) return;
    iframeRef.current?.contentWindow?.postMessage(
      { source: 'databrain-main-frame', type: 'theme', value: theme },
      AI_GALLERY_TARGET_ORIGIN,
    );
  }, [theme, validPath]);

  useEffect(() => {
    if (!validPath) return;
    iframeRef.current?.contentWindow?.postMessage(
      { source: 'databrain-main-frame', type: 'lang', value: lang },
      AI_GALLERY_TARGET_ORIGIN,
    );
  }, [lang, validPath]);

  useEffect(() => {
    if (!validPath) return;
    iframeRef.current?.contentWindow?.postMessage(
      {
        source: 'databrain-main-frame',
        type: 'sidebarCollapsed',
        value: leftMenuCollapsed,
      },
      AI_GALLERY_TARGET_ORIGIN,
    );
  }, [leftMenuCollapsed, validPath]);

  useEffect(() => {
    if (!validPath) return;
    buriedPoint({
      pageId: '701001',
      pageName,
      pagePath: `${stripV2Prefix(location.pathname)}${location.search}`,
      extInfo: JSON.stringify({ pageName, path: validPath }),
      typeId: 'aigc',
    });
  }, [validPath, pageName, location.pathname, location.search]);

  if (!validPath) {
    return <Redirect to={FALLBACK_REDIRECT_PATH} />;
  }

  return (
    <div className="dbh-aigallery-report">
      {isUserInfoReady ? (
        <>
          <ReportToolbar
            path={validPath}
            pageName={pageName}
            iframeRef={iframeRef}
          />
          <iframe
            key={validPath}
            ref={iframeRef}
            title="ai-gallery-report"
            className="dbh-aigallery-report__iframe"
            src={initialSrc}
          />
        </>
      ) : (
        <StatusLoading status="loading" />
      )}
    </div>
  );
};

export default AIGalleryReport;
